import React, { useEffect } from 'react';
import { 
    Box, 
    Card, 
    Table, 
    TableBody, 
    TableContainer, 
    TextField, 
    InputAdornment, 
    IconButton, 
    Switch, 
    Typography, 
    Pagination, 
    TablePagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { GenericTableHead } from 'commons/generics/GenericTableHead';
import { StyledTableCell, StyledTableRow } from 'commons/generics/StyledTableComponents';
import { SinDatos } from 'commons/generics/SinDatos';
import { useTableState } from 'hooks/useTableState';
import { getAllContratosStore, deleteContratoStore, restoreContratoStore } from 'store/contratos';
// import ModalConfirmDeactivation from 'components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from 'components/modals/ModalConfirmRestore';
import { hasPemissions } from 'commons/utils/helpers';
import { ClampedText } from 'commons/generics/ClampedText';
import { ModalConfirmDeactivation } from 'components/modals/ModalConfirmDeactivation';

const headCells = [
    { id: 'name', numeric: false, disablePadding: true, label: 'Nombre' },
    { id: 'created_by', numeric: false, disablePadding: true, label: 'Creado por' },
    { id: 'status', numeric: false, disablePadding: false, label: 'Estado' },
    { id: 'actions', numeric: false, disablePadding: false, label: 'Acciones' },
];

export const TableContratos = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<any>();

    const {
        canView, setCanView,
        canEdit, setCanEdit,
        canDelete, setCanDelete,
        canRestore, setCanRestore,
        openElem, setOpenElem,
        checked,
        searchTerm, setSearchTerm,
        handleSwitchChange,
        handleSearch: handleSearchHook,
        handleResetSearch: handleResetSearchHook,
        page, rowsPerPage,
        handleChangePage, handleChangeRowsPerPage,
        pagePagination, itemCountPagination,
        changePaginationPage: changePaginationPageHook,
        updatePaginationFromList,
        openModalDeactivation, setOpenModalDeactivation,
        openModalRestore, setOpenModalRestore,
        ResponseComponents,
        handleResponse,
        getSortedAndFilteredList,
        isServerSide,
        order,
        orderBy,
        handleRequestSort
    } = useTableState('contratos');

    const [inputValue, setInputValue] = React.useState('');
    const [hasPermission, setHasPermission] = React.useState(true);

    const { contratos_list } = useSelector((state: any) => state.contratos);

   

    useEffect(() => {
        updatePaginationFromList(contratos_list);
    }, [contratos_list, updatePaginationFromList]);

    const checkPermissions = async () => {
        const view = await hasPemissions('modulo.thumano', 'modulo.empleado.contratos', 'view');
        if (!view) setHasPermission(false);
        else setCanView(true);

        const edit = await hasPemissions('modulo.thumano', 'modulo.empleado.contratos', 'edit');
        if (edit) setCanEdit(true);

        const del = await hasPemissions('modulo.thumano', 'modulo.empleado.contratos', 'delete');
        if (del) setCanDelete(true);

        const res = await hasPemissions('modulo.thumano', 'modulo.empleado.contratos', 'restore');
        if (res) setCanRestore(true);
    };

    useEffect(() => {
        checkPermissions();
    }, []);

    const getInitialData = async (status: number = checked ? 0 : 1) => {
        const response = await dispatch(getAllContratosStore({ page: 1, take: rowsPerPage, status }));
        await handleResponse(response, 'listar contratos', false);
    };

    useEffect(() => {
        if (canView) {
            getInitialData();
        }
    }, [canView, rowsPerPage]); // eslint-disable-line

    const handleSearch = async (param: string) => {
        setSearchTerm(param);
        setInputValue(param);
        handleSearchHook(param, dispatch, (p: string) => getAllContratosStore({ page: 1, take: rowsPerPage, status: checked ? 0 : 1, param: p }), getInitialData, null, 'buscar contratos');
    };

    const onSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        handleSwitchChange(e);
        const newChecked = e.target.checked;
        const status = newChecked ? 0 : 1;
        
        if (searchTerm) {
            handleSearchHook(searchTerm, dispatch, (p: string) => getAllContratosStore({ page: 1, take: rowsPerPage, status, param: p }), () => getInitialData(status), null, 'buscar contratos');
        } else {
            getInitialData(status);
        }
    };

    const onResetSearch = () => {
        setInputValue('');
        handleResetSearchHook(() => getInitialData());
    };

    const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
        changePaginationPageHook(event, value);
        const response = await dispatch(getAllContratosStore({ page: value, take: rowsPerPage, status: checked ? 0 : 1, param: searchTerm }));
        await handleResponse(response, 'listar contratos', false);
    };

    const handleView = (row: any) => {
        navigate(`/talento-humano/contratos/${row.id}/detalle`);
    };

    const handleEdit = (row: any) => {
        navigate(`/talento-humano/contratos/${row.id}/editar`);
    };

    const handleDelete = (row: any) => {
        setOpenElem(row);
        setOpenModalDeactivation(true);
    };

    const handleRestoreAction = (row: any) => {
        setOpenElem(row);
        setOpenModalRestore(true);
    };

    const confirmDeactivate = async () => {
        if (!openElem) return;
        const response = await dispatch(deleteContratoStore(openElem.id, { page: page === 0 ? 1 : page, take: rowsPerPage, status: checked ? 0 : 1, param: searchTerm }));
        await handleResponse(response, 'desactivar contrato', true);
        setOpenModalDeactivation(false);
        setOpenElem(null);
    };

    const confirmRestore = async () => {
        if (!openElem) return;
        const response = await dispatch(restoreContratoStore(openElem.id, { page: page === 0 ? 1 : page, take: rowsPerPage, status: checked ? 0 : 1, param: searchTerm }));
        await handleResponse(response, 'restaurar contrato', true);
        setOpenModalRestore(false);
        setOpenElem(null);
    };

    const listData = Array.isArray(contratos_list) ? contratos_list : (contratos_list?.data || []);
    
    const sortedList = getSortedAndFilteredList(
        listData,
        ['name'],
        'status',
        1
    );

    if (!hasPermission) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary">
                    No tienes permisos para ver este contenido.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', mt: 3 }}>
            {ResponseComponents}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
                <TextField
                    placeholder="Buscar..."
                    variant="outlined"
                    size="small"
                    autoComplete='off'
                    value={inputValue}
                    onChange={(e) => handleSearch(e.target.value)}
                    sx={{ width: 400 }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                        endAdornment: inputValue && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={onResetSearch}><ClearIcon /></IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Switch checked={checked} onChange={onSwitchChange} color="primary" />
                    <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
                        Mostrar inactivos
                    </Typography>
                </Box>
            </Box>

            <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none', overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <GenericTableHead 
                            headCells={headCells}
                            order={order as any}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                        />
                        <TableBody>
                            {sortedList.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={4} align="center">
                                        <SinDatos />
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : (
                                sortedList
                                    .slice(isServerSide ? 0 : page * rowsPerPage, isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage)
                                    .map((row: any) => {
                                        const status = row.status ?? (row.isActive ? 1 : 0);
                                        return (
                                            <StyledTableRow key={row.id}>
                                                <StyledTableCell>
                                                    <ClampedText text={row.name} />
                                                </StyledTableCell> 
                                                <StyledTableCell>
                                                    <ClampedText text={row.createdBy.name} />
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            px: 1, 
                                                            py: 0.5, 
                                                            borderRadius: 1, 
                                                            bgcolor: status === 1 ? 'rgba(84, 214, 44, 0.16)' : 'rgba(255, 72, 66, 0.16)', 
                                                            color: status === 1 ? '#229A16' : '#B72136',
                                                            fontWeight: 700
                                                        }}
                                                    >
                                                        {status === 1 ? 'ACTIVO' : 'INACTIVO'}
                                                    </Typography>
                                                </StyledTableCell>
                                                <StyledTableCell align="center">
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                                        <span style={{ cursor: !canView ? 'not-allowed' : 'pointer' }}>
                                                            <IconButton size="small" onClick={() => canView && handleView(row)} disabled={!canView} color="info">
                                                                <VisibilityOutlinedIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                        
                                                        <span style={{ cursor: !canEdit || status !== 1 ? 'not-allowed' : 'pointer' }}>
                                                            <IconButton size="small" onClick={() => canEdit && status === 1 && handleEdit(row)} disabled={!canEdit || status !== 1} color="primary">
                                                                <EditOutlinedIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                        
                                                        {status === 1 ? (
                                                            <span style={{ cursor: !canDelete ? 'not-allowed' : 'pointer' }}>
                                                                <IconButton size="small" onClick={() => canDelete && handleDelete(row)} disabled={!canDelete} color="error">
                                                                    <DeleteOutlineOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        ) : (
                                                            <span style={{ cursor: !canRestore ? 'not-allowed' : 'pointer' }}>
                                                                <IconButton size="small" onClick={() => canRestore && handleRestoreAction(row)} disabled={!canRestore} color="success">
                                                                    <RestoreOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        )}
                                                    </Box>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        );
                                    })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                {sortedList.length > 0 && (
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={sortedList.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Filas por página"
                    />
                )}
                
                {sortedList.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, padding: 2.5, borderTop: 1, borderColor: 'divider' }}>
                        <Pagination
                            sx={{ justifyContent: 'center' }}
                            color="primary"
                            count={itemCountPagination}
                            page={pagePagination}
                            onChange={changePaginationPage} 
                            showFirstButton 
                            showLastButton
                            shape="circular"
                        />
                    </Box>
                )}
            </Card>

            <ModalConfirmDeactivation
                open={openModalDeactivation} 
                setOpen={setOpenModalDeactivation} 
                title='¿Estás seguro que deseas desactivar este contrato?'
                description='Este contrato pasará a estado inactivo.'
                onConfirm={confirmDeactivate}
            />
            <ModalConfirmRestore
                open={openModalRestore}
                setOpen={setOpenModalRestore}
                title='¿Estás seguro que deseas restaurar este contrato?'
                description='Este contrato pasará a estado activo.'
                onConfirm={confirmRestore}
            />
        </Box>
    );
};
